import React, { useCallback, useEffect, useState } from "react";
import { useMounted } from "../../hooks/useMounted";
import { useIntl } from "react-intl";
import vehiclesApi from "../../api/vehicles";
import _, { debounce } from "lodash";
import {
  Button,
  Card,
  Col,
  Flex,
  Image,
  Modal,
  Row,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import VehicleForm from "./VehicleForm";
import ActionBar from "../../components/ActionBar";
import PageTitle from "../../components/PageTitle";
import IntlMessage from "../../components/IntlMessage";
import FilterBar from "../../components/FilterBar";
import PreviewSeatMap from "../seat-map/PreviewSeatmap";

const { Text } = Typography;

const Vehicles = () => {
  const { isMounted } = useMounted();
  const intl = useIntl();
  const [condition, setCondition] = useState({});
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    order_by: "id",
    order_dir: "DESC",
  });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(1);
  const [modal, setModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [reload, setReload] = useState(true);
  const [item, setItem] = useState(null);
  const [openSeatmap, setOpenSeatmap] = useState(false);
  const fetch = useCallback(async () => {
    if (isMounted.current) {
      setLoading(true);
      const res = await vehiclesApi.getAll(filter);
      const data = res?.data;
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(res.total);
      }
      setLoading(false);
    }
  }, [isMounted, filter, reload]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (filter?.filter_name && filter?.filter_value) {
      const newCondition = _.zipObject(
        filter?.filter_name,
        filter?.filter_value
      );
      setCondition(newCondition);
    }
  }, [filter]);

  const onOpenPreviewSeatMap = (record = null) => {
    setOpenSeatmap(true);
    setItem(record);
  };

  const onFilter = debounce(async (value, title, type) => {
    if (type === "search") {
      setFilter((filter) => {
        return { ...filter, keyword: value, page: 1 };
      });
    } else {
      const filter_obj = { ...condition, [title]: value };
      let filter_name = [];
      let filter_value = [];
      for (var key in filter_obj) {
        if (filter_obj[key]) {
          filter_name.push(key);
          filter_value.push(filter_obj[key]);
        }
      }
      setFilter((filter) => {
        return {
          ...filter,
          filter_name: filter_name,
          filter_value: filter_value,
        };
      });
    }
  }, 300);

  const onChangeTable = async (
    pagination,
    filters,
    sorter,
    extra = { currentDataSource: [] }
  ) => {
    setFilter({
      ...filter,
      page: pagination.current,
      limit: pagination.pageSize,
      order_dir: getOrder(sorter.order),
      order_by: sorter.columnKey ? `${sorter.columnKey}` : "id",
    });
  };

  const getOrder = (order) => {
    if (order === "ascend") return "ASC";
    if (order === "descend") return "DESC";
    return "DESC";
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onOpenModal = (record = null) => {
    setModal(true);
    setItem(record);
  };

  const onCloseModal = () => {
    setModal(false);
  };

  const onCopy = async (selectedRowKeys) => {
    if (selectedRowKeys.length > 1) {
      Modal.error({
        title: "Please select only one row",
        okText: intl.formatMessage({ id: "global.close" }),
      });
      return;
    }
    await vehiclesApi.copy(selectedRowKeys).catch((error) => {
      Modal.error({
        title: error.data.message,
        okText: intl.formatMessage({ id: "global.close" }),
      });
      setLoading(false);
    });
    setSelectedRowKeys([]);
    onReload();
  };

  const onReload = () => {
    setReload((reload) => !reload);
  };

  const onDelete = async (selectedRowKeys) => {
    await vehiclesApi.delete(selectedRowKeys).catch((error) => {
      console.error(error);
      Modal.error({
        title: error.data.message,
        okText: intl.formatMessage({ id: "global.close" }),
      });
      setLoading(false);
    });
    setSelectedRowKeys([]);
    onReload();
  };

  const columns = [
    // {
    //   title: <IntlMessage id="global.image" />,
    //   key: "image",
    //   dataIndex: "image",
    //   render: (_, record) => {
    //     return record.image ? <Image width={50} src={record.image} /> : "";
    //   },
    // },
    {
      title: <IntlMessage id="global.title" />,
      key: "title",
      sorter: true,
      dataIndex: "title",
      render: (text, record) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => onOpenModal(record)}
            shape="round"
          >
            {record.title}
          </Button>
        );
      },
    },
    // {
    //   title: <IntlMessage id="global.serial" />,
    //   key: 'serial',
    //   sorter: true,
    //   dataIndex: 'serial',
    //   render: (_, record) => {
    //     return `${record.serial}`;
    //   },
    // },
    {
      title: <IntlMessage id="global.operator" />,
      key: "operator.title",
      sorter: true,
      dataIndex: "operator.title",
      render: (_, record) => {
        return `${record?.operator?.title}`;
      },
    },
    {
      title: <IntlMessage id="global.seat_map" />,
      key: "seatmap.title",
      sorter: true,
      dataIndex: "seatmap.title",
      render: (_, record) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => onOpenPreviewSeatMap(record?.seatmap)}
            shape="round"
          >
            {record?.seatmap?.title}
          </Button>
        );
      },
    },
    {
      title: <IntlMessage id="global.id" />,
      width: 20,
      key: "id",
      sorter: true,
      dataIndex: "id",
      render: (text, record, index) => {
        return record.id;
      },
    },
    {
      title: <IntlMessage id="global.created_at" />,
      key: "created_at",
      dataIndex: "created_at",
      render: (text, record, index) => {
        return (
          <Text>{dayjs(record?.created_at).format("DD/MM/YYYY HH:mm")}</Text>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Flex vertical gap={8}>
        <Row justify="space-between" align={"middle"}>
          <Col>
            <PageTitle title={<IntlMessage id="global.vehicles" />} />
          </Col>

          <Col>
            <ActionBar
              isShowAddButton={true}
              onAdd={onOpenModal}
              deleteMessage={
                "Warning! You are about to delete this vehicles and its associated records. Are you sure you want to delete this record?"
              }
              onCopy={() => onCopy(selectedRowKeys)}
              onRefresh={() => onReload()}
              onDelete={() => onDelete(selectedRowKeys)}
              isShowPublishButtons={false}
              isShowCopyButton={true}
              isShowDeleteButton={true}
              isDisabled={!(selectedRowKeys.length > 0)}
            />
          </Col>
        </Row>

        <Card bordered={false}>
          <Flex vertical gap={8}>
            <FilterBar onFilter={onFilter} />

            <Table
              tableLayout="auto"
              columns={columns}
              dataSource={items}
              onChange={onChangeTable}
              rowKey="id"
              size="small"
              loading={loading}
              scroll={{
                x: "max-content",
              }}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                total: total,
                current: filter.page,
                pageSize: filter.limit,
              }}
              rowSelection={rowSelection}
            />
          </Flex>
        </Card>
      </Flex>

      {modal ? (
        <VehicleForm
          open={modal}
          onClose={onCloseModal}
          currentData={item}
          onReload={onReload}
        />
      ) : null}

      {openSeatmap ? (
        <PreviewSeatMap
          open={openSeatmap}
          onClose={() => setOpenSeatmap(false)}
          onReload={onReload}
          currentData={item}
          isModal={true}
        />
      ) : null}
    </React.Fragment>
  );
};

export default Vehicles;
